/**
 * WebGLTrailLayer — WebGL-accelerated renderer for vehicle trail polylines.
 *
 * Replaces individual google.maps.Polyline instances with deck.gl PathLayer,
 * providing GPU-accelerated rendering for trail segments colored by event type.
 *
 * Supports:
 *   - Event-colored trail segments (matching EventColorMap)
 *   - Click-to-select with blue highlight + white border (video clip request)
 *   - Direction arrows along trail paths (replicates Google Maps polyline icons)
 *   - Real-time point appending during playback animation (addPolylinePoint)
 *
 * Usage in googleProvider.js:
 *   const trailLayer = new WebGLTrailLayer(mapInstance);
 *   trailLayer.addSegment(mapObject);          // add trail polyline segment
 *   trailLayer.addPoint(id, { lat, lng });     // append point during animation
 *   trailLayer.clear();                        // remove all trails
 *   trailLayer.destroy();                      // cleanup
 */

import { GoogleMapsOverlay } from '@deck.gl/google-maps';
import { PathLayer, IconLayer } from '@deck.gl/layers';

/* ─────────── Constants ─────────── */

/** Selected polyline color: #005BF8 */
const SELECTED_COLOR = [0, 91, 248, 255];

/** Selected polyline white border */
const SELECTED_BORDER_COLOR = [255, 255, 255, 204];

const DEFAULT_STROKE_WEIGHT = 8;
const SELECTED_STROKE_EXTRA = 10;
const BORDER_STROKE_WEIGHT = 4;

/** Duration (seconds) of the one-shot glow fade after selection */
const GLOW_DURATION_S = 0.8;

/** Extra width added to the glow layer at the start of the animation */
const GLOW_EXTRA_WIDTH = 8;

/** Glow color: selected blue */
const GLOW_COLOR_BASE = [0, 91, 248];

/** Alpha range: glow starts at GLOW_ALPHA_START and settles at GLOW_ALPHA_END */
const GLOW_ALPHA_START = 90;
const GLOW_ALPHA_END = 30;

/**
 * Screen-space arrow repeat interval in CSS pixels.
 * Matches the original Google Maps polyline symbol: `repeat: '200px'`.
 *
 * At any zoom level, arrows appear every 200 pixels along the rendered path.
 * This means the geographic interval changes with zoom — when zoomed out,
 * each 200px covers more meters, so fewer arrows appear per segment.
 * When zoomed in, 200px covers fewer meters, so more arrows appear.
 * The visual density stays constant and matches the original design.
 */
const ARROW_REPEAT_PX = 200;

/**
 * Arrow triangle SVG — replicates the Google Maps polyline symbol:
 *   path: 'M -2,0 2,0 0,-4 z', scale: 3
 *   strokeOpacity: 0.8, fillOpacity: 0.8
 *
 * Pure white fill so deck.gl's getColor can tint it to the segment color.
 * The triangle is drawn large (48×48 canvas) for crisp rendering and
 * sized down via getSize to match the original ~scale:3 appearance.
 */
const TRAIL_ARROW_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48">
  <path d="M 11,35 37,35 24,9 z" fill="white" stroke="white" stroke-width="1" stroke-linejoin="round"/>
</svg>`;

const TRAIL_ARROW_URL = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(TRAIL_ARROW_SVG);

// Pre-load the arrow image so the icon atlas is ready on first render
const _preloadedArrow = new Image(48, 48);
_preloadedArrow.src = TRAIL_ARROW_URL;

/* ─────────── Geo Utilities ─────────── */

const DEG_TO_RAD = Math.PI / 180;
const RAD_TO_DEG = 180 / Math.PI;
const EARTH_RADIUS = 6371000; // meters

/**
 * Meters per pixel at a given zoom level and latitude.
 * Standard Web Mercator formula used by Google Maps.
 */
const metersPerPixel = (zoom, latDeg) => {
  return 156543.03 * Math.cos(latDeg * DEG_TO_RAD) / Math.pow(2, zoom);
};

/**
 * Haversine distance between two [lng, lat] points in meters.
 */
const haversineDistance = (a, b) => {
  const dLat = (b[1] - a[1]) * DEG_TO_RAD;
  const dLng = (b[0] - a[0]) * DEG_TO_RAD;
  const sinDLat = Math.sin(dLat / 2);
  const sinDLng = Math.sin(dLng / 2);
  const lat1 = a[1] * DEG_TO_RAD;
  const lat2 = b[1] * DEG_TO_RAD;
  const h = sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLng * sinDLng;
  return 2 * EARTH_RADIUS * Math.asin(Math.sqrt(h));
};

/**
 * Forward bearing from point a to point b in degrees [0, 360).
 */
const bearing = (a, b) => {
  const lat1 = a[1] * DEG_TO_RAD;
  const lat2 = b[1] * DEG_TO_RAD;
  const dLng = (b[0] - a[0]) * DEG_TO_RAD;
  const y = Math.sin(dLng) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  return ((Math.atan2(y, x) * RAD_TO_DEG) + 360) % 360;
};

/**
 * Linearly interpolate between two [lng, lat] points.
 */
const lerpPoint = (a, b, t) => [
  a[0] + (b[0] - a[0]) * t,
  a[1] + (b[1] - a[1]) * t,
];

/**
 * Convert hex color (#RRGGBB) to [R, G, B, A] array for deck.gl.
 */
const hexToRgba = (hex, alpha = 153) => {
  if (!hex) return [215, 215, 214, alpha]; // default gray
  const cleaned = hex.replace('#', '');
  const bigint = parseInt(cleaned, 16);
  if (Number.isNaN(bigint)) return [215, 215, 214, alpha];
  return [
    (bigint >> 16) & 255,
    (bigint >> 8) & 255,
    bigint & 255,
    alpha,
  ];
};

/* ─────────── Arrow Sampling ─────────── */

/**
 * Sample arrow positions along a polyline path at regular geographic intervals.
 *
 * @param {Array<[number,number]>} path  Array of [lng, lat] coordinates
 * @param {number} intervalMeters  Distance between arrows in meters
 * @param {string} segmentId  ID of the parent trail segment
 * @returns {Array<{ position: [number,number], angle: number, segmentId: string }>}
 */
const sampleArrowsAlongPath = (path, intervalMeters, segmentId, color) => {
  if (!path || path.length < 2) return [];

  const arrows = [];
  let accumulated = 0;

  for (let i = 1; i < path.length; i++) {
    const prev = path[i - 1];
    const curr = path[i];
    const dist = haversineDistance(prev, curr);

    if (dist < 0.1) continue; // skip near-duplicate points

    const segBearing = bearing(prev, curr);
    let offset = intervalMeters - accumulated;

    while (offset <= dist) {
      const t = offset / dist;
      const point = lerpPoint(prev, curr, t);
      arrows.push({
        position: point,
        // deck.gl IconLayer getAngle: positive = counter-clockwise
        // bearing is clockwise from North
        // We negate so the arrow points in the direction of travel
        angle: -segBearing,
        segmentId,
        color,
      });
      offset += intervalMeters;
    }

    accumulated = dist - (offset - intervalMeters);
  }

  return arrows;
};

/* ─────────── WebGLTrailLayer Class ─────────── */

class WebGLTrailLayer {
  /**
   * @param {google.maps.Map} map  The Google Maps instance
   */
  constructor(map) {
    this._map = map;

    try {
      // Explicit numeric DPR (minimum 2) guarantees the WebGL drawing buffer
      // is always at least 2× CSS pixels for crisp trail lines.
      const _dpr = Math.max(window.devicePixelRatio || 1, 2);
      // ── interleaved: false ──
      // Render trail polylines on a separate deck.gl canvas (same approach
      // as WebGLMarkerLayer). interleaved: true was tried (SGSD-7625) to
      // share the Google Maps vector WebGL context, but that mode fails
      // silently when the shared context is not ready at overlay creation
      // time — the PathLayer simply never paints.
      //
      // Correct stacking order is achieved via _ensureCanvasInteractive(),
      // which inserts the trail canvas inside `.gm-style` BEFORE the
      // WebGL marker canvas. DOM order (no explicit z-index) determines
      // paint sequence, so:
      //   basemap tiles < trail polylines < WebGL unit markers < DOM event pins
      this._overlay = new GoogleMapsOverlay({ interleaved: false, useDevicePixels: _dpr });
      this._overlay.setMap(map);
      this._ensureCanvasInteractive();
    } catch (err) {
      console.error('[WebGLTrail] Failed to create overlay:', err);
    }

    /** @type {Map<string, Object>} segment id → segment data */
    this._segments = new Map();

    /** Currently selected polyline ID (for click-to-highlight) */
    this._selectedId = null;

    /** Data version counter — triggers deck.gl updateTriggers */
    this._version = 0;

    /** Whether the overlay is hidden */
    this._hidden = false;

    /** Cached arrow data (recomputed on segment changes) */
    this._arrowData = [];



    /** Batched render: avoid multiple _renderLayers per frame */
    this._renderScheduled = false;

    /** rAF handle for the glow animation */
    this._animFrameId = null;

    /** Whether the glow animation is running */
    this._animating = false;

    /** Glow animation progress: 0 (start, bright) → 1 (settled, subtle) */
    this._glowProgress = 0;

    /** Timestamp when the current glow animation started (performance.now) */
    this._glowStartTime = 0;

    /** Target frame interval (~30 fps to save GPU) */
    this._frameInterval = 1000 / 30;

    /** Current map zoom — used to calculate screen-space arrow interval */
    this._zoom = this._map.getZoom() || 15;

    /** Zoom listener — re-samples arrows when zoom changes */
    this._zoomListener = this._map.addListener('zoom_changed', () => {
      const newZoom = this._map.getZoom();
      if (newZoom !== this._zoom) {
        this._zoom = newZoom;
        if (this._segments.size > 0 && !this._hidden) {
          this._rebuildArrows();
          this._renderLayers();
        }
      }
    });
  }

  /**
   * Place the deck.gl trail canvas inside `.gm-style`, immediately after
   * the panes container and BEFORE the marker layer's canvas (so trails
   * render below unit markers but above the basemap).
   *
   * See WebGLMarkerLayer._raiseCanvasAboveMarkers for the rationale on
   * why the canvas must live inside `.gm-style` rather than at `mapDiv`
   * level — it's the only way Google Maps' pushed control buttons can
   * render on top without z-index gymnastics. Same idempotency rules.
   * @private
   */
  _ensureCanvasInteractive() {
    const map = this._map;
    const mapDiv = map.getDiv();

    const resolvePanesContainer = (cb) => {
      if (this._panesContainer && this._panesContainer.isConnected) {
        cb(this._panesContainer);
        return;
      }
      const stub = new google.maps.OverlayView(); // eslint-disable-line no-undef
      stub.onAdd = () => {
        const panes = stub.getPanes?.();
        const container = panes?.floatPane?.parentElement || null;
        if (container) this._panesContainer = container;
        try { stub.setMap(null); } catch (_) { /* noop */ }
        cb(this._panesContainer);
      };
      stub.draw = () => {};
      stub.setMap(map);
    };

    let attempts = 0;
    const MAX_ATTEMPTS = 60;

    const place = () => {
      attempts++;
      const allOverlays = mapDiv.querySelectorAll('#deckgl-overlay');
      let canvas = null;
      for (let i = 0; i < allOverlays.length; i++) {
        const el = allOverlays[i];
        // Skip already-tagged overlays (marker layer tags its own).
        if (el.dataset.layerType && el.dataset.layerType !== 'trail') continue;
        canvas = el;
        break;
      }
      if (!canvas || !canvas.parentElement) {
        if (attempts < MAX_ATTEMPTS) requestAnimationFrame(place);
        return;
      }

      canvas.dataset.layerType = 'trail';
      canvas.style.imageRendering = 'crisp-edges';
      canvas.style.imageRendering = '-webkit-optimize-contrast';

      const container = canvas.parentElement;
      // No explicit z-index inside `.gm-style` — DOM order handles stacking.
      container.style.zIndex = '';
      // Trails need pointer events for click picking on segments.
      container.style.pointerEvents = 'auto';

      resolvePanesContainer((panesContainer) => {
        if (!panesContainer || !panesContainer.parentElement) {
          if (attempts < MAX_ATTEMPTS) requestAnimationFrame(place);
          return;
        }
        const gmStyle = panesContainer.parentElement;
        // Insert as the direct sibling AFTER the panes container. If the
        // marker layer's canvas is also inside `.gm-style`, place trails
        // BEFORE it so trails paint underneath the marker arrows.
        const markerCanvas = gmStyle.querySelector('#deckgl-overlay[data-layer-type="markers"]');
        const markerContainer = markerCanvas?.parentElement;
        const desiredNextSibling = (markerContainer && markerContainer.parentElement === gmStyle)
          ? markerContainer
          : panesContainer.nextSibling;
        if (container.parentElement !== gmStyle || container.nextSibling !== desiredNextSibling) {
          gmStyle.insertBefore(container, desiredNextSibling);
        }
      });
    };
    requestAnimationFrame(place);
  }

  /* ─── Public API ─── */

  /**
   * Add a trail polyline segment.
   * Called from googleProvider addMapObject when layerName is VehicleTrails.
   *
   * @param {Object} mapObject  The MapObject with polyline data:
   *   - id, coordinates, color, fillOpacity, strokeWeight,
   *     clickable, trailLineProps, onClickEvent, hideDrawDirection
   */
  addSegment(mapObject) {
    if (!mapObject?.id) return;

    const coordinates = (mapObject.coordinates || []).map(c => {
      const lat = typeof c.lat === 'function' ? c.lat() : parseFloat(c.lat);
      const lng = typeof c.lng === 'function' ? c.lng() : parseFloat(c.lng);
      return [lng, lat];
    }).filter(c => Number.isFinite(c[0]) && Number.isFinite(c[1]));

    const alpha = Math.round((mapObject.fillOpacity || 0.6) * 255);

    // Pre-compute cumulative path lengths for flow-dot positioning
    let totalLength = 0;
    const cumulativeLengths = [0];
    for (let i = 1; i < coordinates.length; i++) {
      totalLength += haversineDistance(coordinates[i - 1], coordinates[i]);
      cumulativeLengths.push(totalLength);
    }

    this._segments.set(mapObject.id, {
      id: mapObject.id,
      coordinates,
      color: hexToRgba(mapObject.color, alpha),
      originalColor: mapObject.color,
      strokeWeight: mapObject.strokeWeight || DEFAULT_STROKE_WEIGHT,
      fillOpacity: mapObject.fillOpacity || 0.6,
      clickable: !!mapObject.clickable,
      trailLineProps: mapObject.trailLineProps || null,
      onClickEvent: mapObject.onClickEvent || null,
      hideDrawDirection: !!mapObject.hideDrawDirection,
      totalLength,
      cumulativeLengths,
    });

    this._scheduleRender();
  }

  /**
   * Update an existing segment (e.g., when the same polyline_id is re-added).
   * If the segment does not exist, it is created via addSegment.
   */
  updateSegment(mapObject) {
    this.addSegment(mapObject);
  }

  /**
   * Append a single point to an existing segment.
   * Used by addPolylinePoint during trail playback animation.
   *
   * @param {string} id  Segment ID (e.g., 'polyline_123')
   * @param {Object} latLng  { lat, lng }
   */
  addPoint(id, latLng) {
    const segment = this._segments.get(id);
    if (!segment || !latLng) return;

    const lat = typeof latLng.lat === 'function' ? latLng.lat() : parseFloat(latLng.lat);
    const lng = typeof latLng.lng === 'function' ? latLng.lng() : parseFloat(latLng.lng);

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

    segment.coordinates.push([lng, lat]);
    this._scheduleRender();
  }

  /**
   * Append multiple points to a segment at once (batch).
   *
   * @param {string} id  Segment ID
   * @param {Array<{lat: number, lng: number}>} latLngs
   */
  addPoints(id, latLngs) {
    const segment = this._segments.get(id);
    if (!segment || !latLngs?.length) return;

    for (let i = 0; i < latLngs.length; i++) {
      const lat = parseFloat(latLngs[i].lat);
      const lng = parseFloat(latLngs[i].lng);
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        segment.coordinates.push([lng, lat]);
      }
    }

    this._scheduleRender();
  }

  /**
   * Check if a segment with the given ID exists.
   */
  hasSegment(id) {
    return this._segments.has(id);
  }

  /**
   * Get the number of trail segments currently loaded.
   */
  getSegmentCount() {
    return this._segments.size;
  }

  /**
   * Remove a specific segment by ID.
   */
  removeSegment(id) {
    if (this._selectedId === id) {
      this._selectedId = null;
    }
    this._segments.delete(id);
    if (this._segments.size === 0) {
      this._stopAnimLoop();
    }
    this._scheduleRender();
  }

  /**
   * Clear all trail segments and reset selection state.
   */
  clear() {
    this._stopAnimLoop();
    this._segments.clear();
    this._selectedId = null;
    this._arrowData = [];
    this._version = 0;
    this._renderLayers();
  }

  /**
   * Show or hide specific segments (matches showMapObjectByLayer for Polyline).
   * @param {boolean} visible
   */
  setSegmentsVisible(visible) {
    this._hidden = !visible;
    if (visible) {
      this._renderLayers();
      this._startAnimLoop();
    } else {
      this._stopAnimLoop();
      if (this._overlay) this._overlay.setProps({ layers: [] });
    }
  }

  /**
   * Show/hide the entire trail overlay.
   */
  setVisible(visible) {
    this._hidden = !visible;
    if (visible) {
      this._renderLayers();
      this._startAnimLoop();
    } else {
      this._stopAnimLoop();
      if (this._overlay) this._overlay.setProps({ layers: [] });
    }
  }

  /**
   * Get the currently selected polyline ID.
   */
  getSelectedId() {
    return this._selectedId;
  }

  /**
   * Programmatically set the selected polyline ID (used by external code
   * that tracks selection state via selectedPolylineIdRef).
   */
  setSelectedId(id) {
    this._selectedId = id;
    this._renderLayers();
  }

  /**
   * Programmatically deselect the current polyline.
   */
  deselect() {
    this._selectedId = null;
    this._renderLayers();
  }

  /**
   * Remove the overlay from the map and clean up resources.
   */
  destroy() {
    this._stopAnimLoop();
    if (this._zoomListener) {
      this._zoomListener.remove();
      this._zoomListener = null;
    }
    // Reset cursor in case a trail was hovered when layer is removed
    if (this._map) {
      try { this._map.setOptions({ draggableCursor: null }); } catch (_) { /* map may be gone */ }
    }
    if (this._overlay) {
      this._overlay.setMap(null);
      this._overlay.finalize();
      this._overlay = null;
    }
    this._segments.clear();
    this._arrowData = [];
    this._selectedId = null;
  }

  /* ─── Click Handling ─── */

  /**
   * Handle click on a trail segment.
   * Replicates the selection + callback behavior from the original
   * google.maps.Polyline click handler in addPolyline().
   *
   * Selection behavior:
   *   - Clicking an unselected segment:  highlight blue + white border, fire callback
   *   - Clicking the same segment again: deselect, restore original color, fire callback
   *   - Clicking a different segment:    deselect old, select new, fire callback
   *
   * The callback (onClickEvent) dispatches trailLineProps to open the
   * clip request drawer for video units.
   *
   * @param {string} segmentId  The clicked segment's ID
   */
  _handleSegmentClick(segmentId) {
    const segment = this._segments.get(segmentId);
    if (!segment || !segment.clickable) return;

    const isSameSelection = this._selectedId === segmentId;

    if (isSameSelection) {
      // Toggle off: deselect
      this._selectedId = null;
      this._stopAnimLoop();
      this._glowProgress = 0;
      if (segment.onClickEvent) {
        segment.onClickEvent({ ...segment.trailLineProps });
      }
      this._rebuildArrows();
      this._renderLayers();
      return;
    }

    // Select the new segment — kick off the one-shot glow
    this._selectedId = segmentId;
    this._glowProgress = 0;
    this._stopAnimLoop();
    if (segment.onClickEvent) {
      segment.onClickEvent({ ...segment.trailLineProps });
    }
    this._rebuildArrows();
    this._renderLayers();
    this._startAnimLoop();
  }

  /* ─── Internal Rendering ─── */

  /**
   * Schedule a render for the next animation frame.
   * Batches multiple addPoint / addSegment calls within a single frame
   * to avoid redundant deck.gl layer rebuilds.
   */
  _scheduleRender() {
    if (this._renderScheduled) return;
    this._renderScheduled = true;
    requestAnimationFrame(() => {
      this._renderScheduled = false;
      this._rebuildArrows();
      this._renderLayers();
      this._startAnimLoop();
    });
  }

  /* ─── Continuous Animation Loop ─── */

  /**
   * Start a one-shot glow animation that fades from bright to subtle
   * over GLOW_DURATION_S seconds, then stops the loop.
   */
  _startAnimLoop() {
    if (this._animating) return;
    if (this._segments.size === 0) return;
    if (!this._selectedId) return;
    this._animating = true;
    this._glowStartTime = performance.now();
    this._animLoop(this._glowStartTime);
  }

  /**
   * Stop the glow animation loop.
   */
  _stopAnimLoop() {
    this._animating = false;
    if (this._animFrameId) {
      cancelAnimationFrame(this._animFrameId);
      this._animFrameId = null;
    }
  }

  /**
   * rAF tick for the one-shot glow. Runs at ~30 fps, stops once the
   * animation finishes and renders a final settled frame.
   */
  _animLoop(now) {
    if (!this._animating || this._hidden || !this._selectedId) {
      this._animating = false;
      return;
    }

    const elapsed = (now - this._glowStartTime) / 1000;

    if (elapsed >= GLOW_DURATION_S) {
      // Animation done — render final settled state and stop
      this._glowProgress = 1;
      this._renderLayers();
      this._animating = false;
      return;
    }

    // Ease-out quad: starts fast, decelerates
    const t = elapsed / GLOW_DURATION_S;
    this._glowProgress = t * (2 - t);
    this._renderLayers();

    this._animFrameId = requestAnimationFrame((ts) => this._animLoop(ts));
  }

  /**
   * Calculate the geographic interval (in meters) between arrows based on
   * the current zoom level. Replicates Google Maps `repeat: '200px'` behavior
   * where the interval adapts to zoom so the same screen-space density is
   * maintained at all zoom levels.
   *
   * @returns {number} Interval in meters
   */
  _getArrowIntervalMeters() {
    const zoom = this._zoom || 15;

    // Use the map center latitude for the Mercator projection correction
    let lat = 30; // reasonable default (Florida / southern US)
    try {
      const center = this._map.getCenter();
      if (center) {
        lat = typeof center.lat === 'function' ? center.lat() : center.lat;
      }
    } catch (_) { /* use default */ }

    const mpp = metersPerPixel(zoom, lat);
    return ARROW_REPEAT_PX * mpp;
  }

  /**
   * Rebuild arrow direction indicator data from all segments.
   * Uses zoom-dependent interval to match Google Maps `repeat: '200px'`.
   * Called before each render pass and on zoom changes.
   */
  _rebuildArrows() {
    const arrows = [];
    const intervalMeters = this._getArrowIntervalMeters();

    for (const [, segment] of this._segments) {
      if (segment.hideDrawDirection) continue;
      if (segment.coordinates.length < 2) continue;

      // When selected, arrows become white to match the selected line appearance;
      // otherwise they inherit the segment's event color.
      const isSelected = segment.id === this._selectedId;
      const arrowColor = isSelected ? [255, 255, 255, 230] : segment.color;
      const segArrows = sampleArrowsAlongPath(
        segment.coordinates,
        intervalMeters,
        segment.id,
        arrowColor
      );
      for (let i = 0; i < segArrows.length; i++) {
        arrows.push(segArrows[i]);
      }
    }

    this._arrowData = arrows;
  }

  /**
   * Build and push all deck.gl layers to the overlay.
   *
   * Layer stack (bottom to top):
   *   0. Solid trail segments — base event colors (unselected)
   *   1. Solid selected segment (blue, wider)
   *   2. Glow — one-shot ease-out fade behind selected segment
   *   3. Selected border — white thin line on top
   *   4. Direction arrows — IconLayer
   *
   * When a segment is first selected, a short animation fades the glow
   * from bright to subtle; after that, a static subtle glow remains.
   */
  _renderLayers() {
    if (!this._overlay) return;

    if (this._hidden) {
      this._overlay.setProps({ layers: [] });
      return;
    }

    this._version += 1;
    const layers = [];
    const allSegments = Array.from(this._segments.values());

    if (allSegments.length === 0) {
      this._overlay.setProps({ layers: [] });
      return;
    }

    // Filter to renderable segments (≥2 coordinates)
    const renderable = allSegments.filter(s => s.coordinates.length >= 2);

    if (renderable.length === 0) {
      this._overlay.setProps({ layers: [] });
      return;
    }

    const selectedId = this._selectedId;

    const unselected = renderable.filter(s => s.id !== selectedId);
    const selected = renderable.find(s => s.id === selectedId) || null;

    // ── 0. Solid trail segments (unselected) ──
    if (unselected.length > 0) {
      layers.push(new PathLayer({
        id: 'webgl-trail-segments',
        data: unselected,
        pickable: true,
        getPath: d => d.coordinates,
        getColor: d => d.color,
        getWidth: d => d.strokeWeight,
        widthUnits: 'pixels',
        widthMinPixels: 2,
        widthMaxPixels: 24,
        capRounded: true,
        jointRounded: true,
        onClick: (info) => {
          if (info.object) {
            this._handleSegmentClick(info.object.id);
            return true;
          }
        },
        onHover: (info) => {
          if (info.object && info.object.clickable) {
            this._map.setOptions({ draggableCursor: 'pointer' });
          } else {
            this._map.setOptions({ draggableCursor: null });
          }
        },
        updateTriggers: {
          getPath: this._version,
          getColor: this._version,
          getWidth: this._version,
        },
      }));
    }

    // ── 1. Solid selected segment (blue, wider) ──
    if (selected) {
      layers.push(new PathLayer({
        id: 'webgl-trail-selected-solid',
        data: [selected],
        pickable: true,
        getPath: d => d.coordinates,
        getColor: SELECTED_COLOR,
        getWidth: (selected.strokeWeight || DEFAULT_STROKE_WEIGHT) + SELECTED_STROKE_EXTRA,
        widthUnits: 'pixels',
        widthMinPixels: 4,
        widthMaxPixels: 32,
        capRounded: true,
        jointRounded: true,
        onClick: (info) => {
          if (info.object) {
            this._handleSegmentClick(info.object.id);
            return true;
          }
        },
        onHover: (info) => {
          if (info.object && info.object.clickable) {
            this._map.setOptions({ draggableCursor: 'pointer' });
          } else {
            this._map.setOptions({ draggableCursor: null });
          }
        },
        updateTriggers: {
          getPath: this._version,
        },
      }));

      // ── 3. Pulse glow behind selected segment ──
      // One-shot ease-out: progress goes 0 → 1, glow fades from bright to subtle
      const p = this._glowProgress || 0;
      const glowAlpha = Math.round(GLOW_ALPHA_START + (GLOW_ALPHA_END - GLOW_ALPHA_START) * p);
      const glowExtraW = Math.round(GLOW_EXTRA_WIDTH * (1 - p));
      const glowWidth = (selected.strokeWeight || DEFAULT_STROKE_WEIGHT) + SELECTED_STROKE_EXTRA + 4 + glowExtraW;

      layers.push(new PathLayer({
        id: 'webgl-trail-selected-glow',
        data: [selected],
        pickable: false,
        getPath: d => d.coordinates,
        getColor: [...GLOW_COLOR_BASE, glowAlpha],
        getWidth: glowWidth,
        widthUnits: 'pixels',
        widthMinPixels: 6,
        widthMaxPixels: 48,
        capRounded: true,
        jointRounded: true,
        updateTriggers: {
          getPath: this._version,
          getColor: this._glowProgress,
          getWidth: this._glowProgress,
        },
      }));

      // ── 4. White border on top of selected ──
      layers.push(new PathLayer({
        id: 'webgl-trail-selected-border',
        data: [selected],
        pickable: true,
        getPath: d => d.coordinates,
        getColor: SELECTED_BORDER_COLOR,
        getWidth: BORDER_STROKE_WEIGHT,
        widthUnits: 'pixels',
        widthMinPixels: 1,
        capRounded: true,
        jointRounded: true,
        onClick: (info) => {
          if (info.object) {
            this._handleSegmentClick(info.object.id);
            return true;
          }
        },
        updateTriggers: {
          getPath: this._version,
        },
      }));
    }

    // ── 5. Direction arrows along all trail paths ──
    if (this._arrowData.length > 0) {
      layers.push(new IconLayer({
        id: 'webgl-trail-direction-arrows',
        data: this._arrowData,
        pickable: false,
        getPosition: d => d.position,
        getAngle: d => d.angle,
        getIcon: () => ({
          url: TRAIL_ARROW_URL,
          width: 48,
          height: 48,
          anchorX: 24,
          anchorY: 24,
          mask: true,
        }),
        getColor: d => d.color || [0, 0, 0, 200],
        getSize: 34,
        sizeUnits: 'pixels',
        sizeMinPixels: 22,
        sizeMaxPixels: 45,
        billboard: false,
        updateTriggers: {
          getPosition: this._version,
          getAngle: this._version,
          getColor: this._version,
        },
      }));
    }

    this._overlay.setProps({ layers });
  }
}

export { WebGLTrailLayer };
